import React, { useState } from 'react';
import { useAppDispatch } from 'hooks/redux';
import { find, isEqual, sortBy } from 'lodash';
import { setElementValues } from 'store/rfiSlice';
import { RadioButton, RadioButtonChangeEvent } from '@progress/kendo-react-inputs';
import { answerValueTypes, ElementAnswer, ElementOption, elementTypes, IElementProps, optionTypes } from '../../types/RfiTypes';
import { OTHER_DEFAULT_LABEL } from './ElementEditor';

const RadioGroup = (props: IElementProps) => {
  // Hooks
  const dispatch = useAppDispatch();

  // State
  const options = sortBy(props.element.options, (option: ElementOption) => {
    return option.sequence;
  });
  let otherOption = find(props.element.options, function (o: ElementOption) {
    return o.type === optionTypes.OTHER;
  });
  let defaultValue = find(props.element.answers, function (o: ElementAnswer) {
    return o.answerValueType === answerValueTypes.OPTION;
  })?.value;
  const [checked, setChecked] = useState<string | null>(() => {
    return (
      props.element.answers?.find(
        (a: ElementAnswer) => a.answerValueType === answerValueTypes.OPTION
      )?.value ?? null
    );
  });
  const [otherValue, setOtherValue] = useState<string>(
    () =>
      find(props.element.answers, (a: ElementAnswer) => {
        return a.answerValueType === answerValueTypes.OTHER;
      })?.value ?? ""
  );

  // Handlers
  const handleChange = (e: RadioButtonChangeEvent) => {
    let newValues = [
      { value: e.value, answerValueType: answerValueTypes.OPTION },
    ];

    if (otherOption) {
      newValues.push({
        value: otherValue ?? "",
        answerValueType: answerValueTypes.OTHER,
      });
    }

    setChecked(e.value);
    //props.element.setValues(newValues);
    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: newValues,
      })
    );
  };

  const handleChangeOther = (event: React.FormEvent<HTMLInputElement>) => {
    setOtherValue(event.currentTarget.value ?? "");

    // props.element.setValues([
    //   {
    //     value: checked ? checked : "",
    //     answerValueType: answerValueTypes.OPTION,
    //   },
    //   {
    //     value: event.currentTarget.value ?? "",
    //     answerValueType: answerValueTypes.OTHER,
    //   },
    // ]);

    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: [
          {
            value: checked ? checked : "",
            answerValueType: answerValueTypes.OPTION,
          },
          {
            value: event.currentTarget.value ?? "",
            answerValueType: answerValueTypes.OTHER,
          },
        ],
      })
    );
  };

  // Render
  if (props.readOnly) {
    return (
      <div>
        <div className="col-form-label">
          {props.element.elementType === elementTypes.SURVEY
            ? `${props.questionNumber}. `
            : ""}{" "}
          {props.element.fieldLabel}{" "}
          {props.overrideRequired === undefined ? (
            props.element.requiredResponse ? (
              <span>*</span>
            ) : (
              ""
            )
          ) : props.overrideRequired === true ? (
            <span>*</span>
          ) : (
            ""
          )}
        </div>
        <div>Options:</div>

        {options
          ? options.map((option: ElementOption, index: number) => {
              return (
                option.type === optionTypes.DEFAULT && (
                  <div style={{ marginLeft: "20px" }} key={index}>
                    {checked === option.value ? (
                      <span className="k-icon k-i-radiobutton-checked"></span>
                    ) : (
                      <span className="k-icon k-i-radiobutton"></span>
                    )}{" "}
                    {`${
                      option?.description ?? `Option ${option?.sequence ?? ""}`
                    }`}{" "}
                    {checked === option.value ? " (Selected)" : ""}
                    <br />
                  </div>
                )
              );
            })
          : ""}
        {otherOption && (
          <div style={{ marginLeft: "20px", marginRight: "10px" }}>
            <label
              htmlFor={`other-value-${props.element.elementId}`}
              className="col-form-label"
            >
              {otherOption?.description ?? OTHER_DEFAULT_LABEL}
            </label>
            <input
              id={`other-value-${props.element.elementId}`}
              className="form-control"
              value={otherValue}
              disabled
            />
            <br />
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div>
        <fieldset className="usa-fieldset-inputs usa-sans">
          <legend className="visuallyHidden">{props.element.fieldLabel}</legend>

          <div className="col-form-label">
            {props.element.elementType === elementTypes.SURVEY
              ? `${props.questionNumber}. `
              : ""}{" "}
            {props.element.fieldLabel}{" "}
            {props.overrideRequired === undefined ? (
              props.element.requiredResponse ? (
                <span className="requiredField">(required)</span>
              ) : (
                ""
              )
            ) : props.overrideRequired === true ? (
              <span className="requiredField">(required)</span>
            ) : (
              ""
            )}
          </div>

          {options
            ? options.map((option: ElementOption, index: number) => {
                //console.log(`${option.value} - ${checked}`);
                return (
                  option.type === optionTypes.DEFAULT && (
                    <div style={{ marginLeft: "20px" }} key={index}>
                      <RadioButton
                        id={`${option.value}`}
                        name={`cb-${props.element.elementId}`}
                        value={option.value}
                        label={
                          option?.description ??
                          `Option ${option?.sequence ?? ""}`
                        }
                        key={option.value}
                        onChange={handleChange}
                        checked={checked === option.value}
                      />
                      <br />
                    </div>
                  )
                );
              })
            : ""}

          {otherOption && (
            <div style={{ marginLeft: "20px", marginRight: "10px" }}>
              <label
                className="col-form-label"
                htmlFor={`other-value-${props.element.elementId}`}
              >
                {otherOption?.description ?? OTHER_DEFAULT_LABEL}
              </label>
              <input
                id={`other-value-${props.element.elementId}`}
                className="form-control"
                value={otherValue}
                disabled={props.readOnly}
                onChange={handleChangeOther}
              />
            </div>
          )}
        </fieldset>
      </div>
    );
  }
};

const areEqual = (prevProps: IElementProps, nextProps: IElementProps) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const RadioElement = React.memo(RadioGroup, areEqual);

export default RadioElement;
